import SectionHeader from "@/components/feature/panel/SectionHeader";
import EmptyState from "@/components/feature/panel/EmptyState";
import PanelLinks from "@/components/feature/panel/PanelLinks";
import ChildSelector from "@/pages/veli/components/ChildSelector";
import { useVeliChild, useChildCourses } from "@/hooks/useVeli";

export default function Dersler() {
  const { children, activeChild, activeUserId, setActiveUserId, loading: childrenLoading } =
    useVeliChild();
  const { data, loading, error, reload } = useChildCourses(activeChild?.userId ?? null);

  if (childrenLoading) return <EmptyState icon="ri-loader-4-line" title="Yükleniyor..." />;

  if (children.length === 0) {
    return (
      <div className="space-y-6">
        <SectionHeader
          title="Dersler"
          description="Öğrencinin derslerini ve ilerlemesini takip et."
        />
        <EmptyState
          icon="ri-book-2-line"
          title="Henüz öğrenci hesabı bağlanmadı"
          description="Çocuğunuzun hesabını eşleştirdiğinizde dersleri ve ilerlemesi burada görünecek."
        />
        <PanelLinks
          links={[{ label: "Çocuğunu Eşleştir", href: "/veli/profil", icon: "ri-link" }]}
        />
      </div>
    );
  }

  const list = data ?? [];

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Dersler"
        description={`${activeChild?.fullName ?? "Öğrencinin"} dersleri ve konu ilerlemesi.`}
      />
      <ChildSelector
        children={children}
        activeUserId={activeUserId}
        onSelect={setActiveUserId}
      />

      {loading ? (
        <EmptyState icon="ri-loader-4-line" title="Yükleniyor..." />
      ) : error ? (
        <div className="rounded-lg border border-background-200 bg-background-50 p-6 text-center">
          <p className="text-sm text-foreground-600">{error}</p>
          <button
            type="button"
            onClick={reload}
            className="mt-4 inline-flex items-center gap-2 whitespace-nowrap px-4 py-2 rounded-md text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors"
          >
            <i className="ri-refresh-line" aria-hidden="true"></i>
            Tekrar dene
          </button>
        </div>
      ) : list.length === 0 ? (
        <EmptyState
          icon="ri-book-2-line"
          title="Henüz ders kaydı yok"
          description="Dersler eklendikçe çocuğunuzun ilerlemesi burada görünecek."
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {list.map((course) => (
            <div
              key={course.id}
              className="rounded-lg border border-background-200 bg-background-50 p-5 hover:border-accent-300 transition-colors"
            >
              <div className="flex items-center gap-3">
                <span className={`w-11 h-11 flex items-center justify-center rounded-lg ${course.color}`}>
                  <i className={`${course.icon} text-xl`} aria-hidden="true"></i>
                </span>
                <div className="min-w-0">
                  <h3 className="font-heading font-semibold text-base text-foreground-950 truncate">
                    {course.name}
                  </h3>
                  <p className="text-xs text-foreground-500 mt-0.5">{course.teacher || "—"}</p>
                </div>
              </div>

              <div className="mt-4">
                <div className="flex items-center justify-between text-xs text-foreground-500">
                  <span>
                    {course.completedTopics} / {course.totalTopics} konu
                  </span>
                  <span className="font-semibold text-foreground-700">%{course.progress}</span>
                </div>
                <div className="mt-1.5 h-2 rounded-full bg-background-100 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-primary-500 transition-all"
                    style={{ width: `${Math.min(course.progress, 100)}%` }}
                  />
                </div>
              </div>

              {course.weakCount > 0 && (
                <p className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-accent-700">
                  <i className="ri-alert-line" aria-hidden="true"></i>
                  {course.weakCount} eksik konu
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      <PanelLinks
        links={[
          { label: "Deneme Sonuçları", href: "/veli/deneme-sonuclari", icon: "ri-file-text-line" },
          { label: "Haftalık Rapor", href: "/veli/haftalik-rapor", icon: "ri-bar-chart-line" },
        ]}
      />
    </div>
  );
}